/**
 * A PARENT SCREEN — level 2 (09 §1.4). `/c/{company_uid}/m/{module}/{parent}`.
 *
 * This is the shape every parent screen takes: a title, a filter summary in words, one grid, and a
 * back-to-parent that goes UP EXACTLY ONE LEVEL (09 §1.4 — never to the module home, never
 * browser-history-back). It is deliberately empty of business data: no business module is `active`
 * yet, so inventing rows here would be the "documented as working, reaching nothing" pattern.
 *
 * 09 §5.4 T12: zero rows is a written state, never a grid with headers and nothing under them.
 */

import { useEffect } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { routes } from '../routes';

export interface ParentScreenPlaceholderProps {
  readonly companyUid: string;
  readonly moduleCode: string;
  readonly parent: string;
}

export function ParentScreenPlaceholder({
  companyUid,
  moduleCode,
  parent,
}: ParentScreenPlaceholderProps): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const { setCompanyByUid, setModuleByCode, setEntityLabel } = useDeclareScreenContext();

  useEffect(() => {
    setCompanyByUid(companyUid);
    setModuleByCode(moduleCode);
    setEntityLabel(null);
  }, [companyUid, moduleCode, setCompanyByUid, setModuleByCode, setEntityLabel]);

  const moduleNameHe = ctx.module?.displayNameHe ?? '';

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={parent}
      // Up exactly one level: the module home, which is this screen's parent.
      backToParent={{ labelHe: moduleNameHe, href: routes.moduleHome(companyUid, moduleCode) }}
    >
      <DataTable<never>
        captionHe={parent}
        columns={[]}
        rows={[]}
        rowKey={() => ''}
        emptyStateHe={
          ctx.period === null
            ? t('placeholder.noData')
            : `${t('placeholder.noDataFor')} ${ctx.period.displayHe}.`
        }
      />
    </AppShell>
  );
}
